import React, { useState, useRef, useEffect, useCallback } from 'react'
import { postRequest } from '@react/helpers/fetch'
import consumer from 'channels/consumer'

const DEBOUNCE_MS      = 500
const MIN_QUERY_LENGTH = 3

const CATEGORY_ICONS = {
  workorders:              'fa-wrench',
  workorder_demands:       'fa-comment-o',
  clients:                 'fa-user',
  sites:                   'fa-map-marker',
  materials:               'fa-cogs',
  zones:                   'fa-th-large',
  meters:                  'fa-tachometer',
  quotes:                  'fa-file-text-o',
  invoices:                'fa-file-text',
  products:                'fa-cubes',
  purchase_orders:         'fa-shopping-cart',
  purchase_invoices:       'fa-credit-card',
  suppliers:               'fa-truck',
  'maintenance/contracts': 'fa-handshake-o',
  contacts:                'fa-address-book-o',
}

const MultiSearch = ({ searchPath, deepSearchPath, search }) => {
  const [query, setQuery]                         = useState(search || '')
  const [results, setResults]                     = useState(null)
  const [loading, setLoading]                     = useState(false)
  const [deepSearchLoading, setDeepSearchLoading] = useState(false)
  const [deepSearchDone, setDeepSearchDone]       = useState(false)
  const [open, setOpen]                           = useState(false)
  const containerRef                              = useRef(null)
  const debounceRef                               = useRef(null)
  const subscriptionRef                           = useRef(null)

  const cleanupSubscription = () => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }
  }

  const performSearch = useCallback((query) => {
    if (query.length < MIN_QUERY_LENGTH) {
      setResults(null)
      setOpen(false)
      return
    }

    cleanupSubscription()
    setDeepSearchLoading(false)
    setDeepSearchDone(false)
    setLoading(true)
    setOpen(true)
    postRequest({ url: searchPath, body: { query } })
      .then(data => {
        setResults(data)
      })
      .catch(() => setResults(null))
      .finally(() => setLoading(false))
  }, [searchPath])

  const performDeepSearch = useCallback(() => {
    if (deepSearchLoading || query.length < MIN_QUERY_LENGTH) return

    setDeepSearchLoading(true)
    postRequest({ url: deepSearchPath, body: { query } })
      .then(data => {
        const { search_id } = data
        const params = { channel: 'MultiSearchChannel', search_id }

        subscriptionRef.current = consumer.subscriptions.create(params, {
          received: (data) => {
            if (data.status === 'ok') {
              setResults(data.results)
              setDeepSearchDone(true)
            }
            setDeepSearchLoading(false)
            cleanupSubscription()
          }
        })
      })
      .catch(() => setDeepSearchLoading(false))
  }, [deepSearchPath, query, deepSearchLoading])

  const handleChange = (e) => {
    const value = e.target.value
    setQuery(value)

    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => performSearch(value), DEBOUNCE_MS)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      clearTimeout(debounceRef.current)
      performSearch(query)
    }
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current)
      cleanupSubscription()
    }
  }, [])

  const categories = results ? Object.entries(results).filter(([, v]) => v) : []
  const hasResults = categories.length > 0
  const isSearching = loading || deepSearchLoading

  return (
    <div className="multi-search p-relative w-100" ref={containerRef}>
      <div className="multi-search__input-wrapper p-relative w-100">
        <input
          type="text"
          className="multi-search__input px-2"
          placeholder={I18n.t('navigation.search_placeholder')}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results) setOpen(true) }}
        />
        <div className="multi-search__icon">
          {loading
            ? <i className="fa fa-spinner fa-spin" />
            : <i className="fa fa-search" />
          }
        </div>
      </div>

      {open && (
        <div className="multi-search__dropdown">
          {isSearching && (
            <div className="multi-search__loading f-md">
              <i className="fa fa-spinner fa-spin mr-1" />
              {I18n.t('navigation.search_placeholder')}
            </div>
          )}
          {!isSearching && hasResults && (
            categories.map(([key, category]) => (
              <div key={key} className="multi-search__category">
                <div className="multi-search__category-header f-xs">
                  {CATEGORY_ICONS[key] && <i className={`fa ${CATEGORY_ICONS[key]}`} />}
                  <span>{category.label}</span>
                </div>
                <ul className="multi-search__items">
                  {category.items.map(item => (
                    <li key={item.id} className="multi-search__item">
                      <a href={item.path} className="f-md">{item.name}</a>
                    </li>
                  ))}
                </ul>
                {category.has_more && (
                  <a href={category.index_path} className="multi-search__see-more f-sm">
                    {I18n.t('navigation.see_all')} ({category.count}+)
                  </a>
                )}
              </div>
            ))
          )}
          {!isSearching && !hasResults && (
            <div className="multi-search__empty f-md">
              {I18n.t('navigation.nothing_found_yet')}
            </div>
          )}
          {!loading && !deepSearchDone && (
            <div className="multi-search__deep-search">
              <button
                className="multi-search__deep-search-btn f-sm"
                onClick={performDeepSearch}
                disabled={deepSearchLoading}
              >
                {deepSearchLoading
                  ? <><i className="fa fa-spinner fa-spin mr-1" />{I18n.t('navigation.deep_search')}</>
                  : <><i className="fa fa-search-plus mr-1" />{I18n.t('navigation.deep_search')}</>
                }
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MultiSearch
